// Moteur d'une calculatrice rudimentaire (saisie nombre → opérateur → nombre → =).
// Pur et sans état React : tout passe par `calcReducer`, ce qui le rend testable.

export type CalcOperator = '+' | '-' | '×' | '÷';

export interface CalcState {
  /** Nombre affiché en cours de saisie. */
  display: string;
  /** Opérande mémorisé (gauche de l'opération), ou null. */
  operand: number | null;
  /** Opérateur en attente, ou null. */
  operator: CalcOperator | null;
  /** Si true, la prochaine saisie de chiffre remplace `display` au lieu de l'allonger. */
  overwrite: boolean;
  /** true quand un calcul a produit une erreur (division par zéro). */
  error: boolean;
}

export type CalcAction =
  | { type: 'digit'; value: string }
  | { type: 'decimal' }
  | { type: 'operator'; value: CalcOperator }
  | { type: 'equals' }
  | { type: 'clear' }
  | { type: 'backspace' }
  | { type: 'negate' }
  | { type: 'percent' };

export const INITIAL_CALC_STATE: CalcState = {
  display: '0',
  operand: null,
  operator: null,
  overwrite: false,
  error: false,
};

const MAX_DISPLAY_DIGITS = 12;

/** Formate un nombre pour l'affichage sans notation exponentielle inutile ni zéros traînants. */
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return 'Erreur';
  // Arrondit pour éviter les artefacts flottants (0.1 + 0.2), puis nettoie.
  const rounded = Number(n.toPrecision(MAX_DISPLAY_DIGITS));
  return String(rounded);
}

function compute(a: number, b: number, op: CalcOperator): number {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '×':
      return a * b;
    case '÷':
      return b === 0 ? NaN : a / b;
  }
}

export function calcReducer(state: CalcState, action: CalcAction): CalcState {
  // Toute action sauf « clear » est ignorée tant qu'on est en erreur.
  if (state.error && action.type !== 'clear') return state;

  switch (action.type) {
    case 'clear':
      return INITIAL_CALC_STATE;

    case 'digit': {
      if (state.overwrite) {
        return { ...state, display: action.value, overwrite: false };
      }
      if (state.display === '0') {
        return { ...state, display: action.value };
      }
      if (state.display.replace(/[-.]/g, '').length >= MAX_DISPLAY_DIGITS) {
        return state;
      }
      return { ...state, display: state.display + action.value };
    }

    case 'decimal': {
      if (state.overwrite) {
        return { ...state, display: '0.', overwrite: false };
      }
      if (state.display.includes('.')) return state;
      return { ...state, display: state.display + '.' };
    }

    case 'operator': {
      const current = Number(state.display);
      // Chaîne d'opérations : si un opérateur est déjà posé et qu'on a saisi un
      // second opérande, on calcule le résultat intermédiaire d'abord.
      if (state.operator !== null && state.operand !== null && !state.overwrite) {
        const result = compute(state.operand, current, state.operator);
        if (Number.isNaN(result)) {
          return { ...INITIAL_CALC_STATE, display: 'Erreur', error: true };
        }
        return {
          display: formatNumber(result),
          operand: result,
          operator: action.value,
          overwrite: true,
          error: false,
        };
      }
      return {
        ...state,
        operand: current,
        operator: action.value,
        overwrite: true,
      };
    }

    case 'equals': {
      if (state.operator === null || state.operand === null) return state;
      const current = Number(state.display);
      const result = compute(state.operand, current, state.operator);
      if (Number.isNaN(result)) {
        return { ...INITIAL_CALC_STATE, display: 'Erreur', error: true };
      }
      return {
        display: formatNumber(result),
        operand: null,
        operator: null,
        overwrite: true,
        error: false,
      };
    }

    case 'backspace': {
      if (state.overwrite) return state;
      if (
        state.display.length <= 1 ||
        (state.display.length === 2 && state.display.startsWith('-'))
      ) {
        return { ...state, display: '0' };
      }
      return { ...state, display: state.display.slice(0, -1) };
    }

    case 'negate': {
      if (state.display === '0') return state;
      return {
        ...state,
        display: state.display.startsWith('-') ? state.display.slice(1) : '-' + state.display,
      };
    }

    case 'percent': {
      const value = Number(state.display) / 100;
      return { ...state, display: formatNumber(value), overwrite: true };
    }
  }
}
